import {
  ForwardedRef,
  forwardRef,
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { ClickAwayListener, SxProps } from '@mui/material'
import clsx from 'clsx'
import { Box } from '@/components/Layout'
import { Popper } from '@/components/Surface'

import DropdownButton from './DropdownButton'
import { DropdownProps } from './Dropdown.types'
import { dropdownStyles as styles } from './Dropdown.style'

const Dropdown = forwardRef(
  (
    {
      open = false,
      onOpen,
      onClose,
      label,
      labelHeading = '',
      children,
      onClick,
      buttonProps = {},
      fullWidth = false,
      fullWidthWithSpace = false,
      wrapperProps = {},
      contentWrapperProps = {},
      popperProps = {},
    }: DropdownProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [isDropDownOpen, setIsDropDownOpen] = useState(open)

    // refs management
    const { ref: buttonRefProp, className: buttonClassname = '', ...otherButtonProps } = buttonProps
    const { sx: popperSx, wrapperSx: popperWrapperSx, ...otherPopperProps } = popperProps
    const buttonRefInner = useRef<HTMLButtonElement>(null)
    const buttonRef = buttonRefProp ?? buttonRefInner

    const getAnchorElForDropdown = useCallback((reference: typeof buttonRef) => {
      if (typeof reference === 'function') return null

      return reference && 'current' in reference ? reference.current : null
    }, [])

    const onDropdownOpen = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        // if onClick is provided in that case dropdown will be controlled by the parent component
        if (onClick) return onClick(event)

        setIsDropDownOpen(true)
        onOpen?.()
      },
      [onClick, onOpen],
    )

    const onDropdownClose = useCallback(() => {
      setIsDropDownOpen(false)
      onClose?.()
    }, [onClose])

    /**
     * Synchronize the internal state with the open prop.
     */
    useEffect(() => {
      setIsDropDownOpen(open)
    }, [open])

    const { sx: wrapperSx, className: wrapperClassName = '', ...otherWrapperProps } = wrapperProps
    const { sx: contentWrapperSx, ...otherContentWrapperProps } = contentWrapperProps

    return (
      <Box
        ref={ref}
        sx={[styles.wrapper, wrapperSx] as SxProps}
        className={clsx({ 'w-100': fullWidth, [wrapperClassName]: Boolean(wrapperClassName) })}
        {...otherWrapperProps}>
        <DropdownButton
          ref={buttonRef}
          className={clsx({
            [buttonClassname]: Boolean(buttonClassname),
          })}
          label={label}
          labelHeading={labelHeading}
          active={isDropDownOpen}
          onClick={onDropdownOpen}
          fullWidth={fullWidth}
          fullWidthWithSpace={fullWidthWithSpace}
          {...otherButtonProps}
        />

        <Popper
          wrapperSx={{ ...styles.popover, ...popperWrapperSx } as SxProps}
          open={isDropDownOpen}
          anchorEl={getAnchorElForDropdown(buttonRef)}
          sx={popperSx}
          {...otherPopperProps}>
          <ClickAwayListener onClickAway={onDropdownClose}>
            <Box sx={[styles.content, contentWrapperSx] as SxProps} {...otherContentWrapperProps}>
              {children}
            </Box>
          </ClickAwayListener>
        </Popper>
      </Box>
    )
  },
)

Dropdown.displayName = 'Dropdown'

export default memo(Dropdown)
